import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';

import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { ChevronRight } from "lucide-react"

import { cn } from "@/lib/utils"

const Breadcrumb = React.forwardRef<
  HTMLElement,
  React.HTMLAttributes<HTMLElement>
>(({ className, ...props }, ref) => { useLanguage(); return ((
  <nav
    ref={ref}
    aria-label={translateInterfaceText("breadcrumb")}
    className={cn(
      "inline-flex items-center space-x-1 md:space-x-2 text-sm",
      className
    )}
    {...props}
  />
)); })
Breadcrumb.displayName = "Breadcrumb"

const BreadcrumbList = React.forwardRef<
  HTMLOListElement,
  React.OlHTMLAttributes<HTMLOListElement>
>(({ className, ...props }, ref) => { useLanguage(); return ((
  <ol
    ref={ref}
    className={cn("flex items-center space-x-1 md:space-x-2", className)}
    {...props}
  />
)); })
BreadcrumbList.displayName = "BreadcrumbList"

const BreadcrumbItem = React.forwardRef<
  HTMLLIElement,
  React.LiHTMLAttributes<HTMLLIElement>
>(({ className, ...props }, ref) => { useLanguage(); return ((
  <li
    ref={ref}
    className={cn("inline-flex items-center", className)}
    {...props}
  />
)); })
BreadcrumbItem.displayName = "BreadcrumbItem"

// Define the interface separately before using it
interface AnchorElementWithRef extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  asChild?: boolean
}

const BreadcrumbLink = React.forwardRef<
  HTMLAnchorElement,
  AnchorElementWithRef
>(({ className, asChild, ...props }, ref) => {
  useLanguage();
  const Component = asChild ? Slot : "a"

  return (
    <Component
      ref={ref}
      className={cn(
        "text-sm font-medium text-[#2563EB] hover:text-[#1d4ed8] transition-colors cursor-pointer",
        className
      )}
      {...props}
    />
  )
})
BreadcrumbLink.displayName = "BreadcrumbLink"

const BreadcrumbSeparator = ({
  children,
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => { useLanguage(); return ((
  <span
    role="presentation"
    aria-hidden="true"
    className={cn("text-[#94A3B8] [&>svg]:size-3.5", className)}
    {...props}
  >
    {children || <ChevronRight className="h-4 w-4" />}
  </span>
)); }
BreadcrumbSeparator.displayName = "BreadcrumbSeparator"

const BreadcrumbEllipsis = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => { useLanguage(); return ((
  <span
    role="presentation"
    aria-hidden="true"
    className={cn("ml-1 inline h-4 w-4", className)}
    {...props}
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      className="h-4 w-4"
    >
      <path
        d="M4 12a2 2 0 1 1 4 0 2 2 0 0 1-4 0zm6 0a2 2 0 1 1 4 0 2 2 0 0 1-4 0zm6 0a2 2 0 1 1 4 0 2 2 0 0 1-4 0z"
        fill="currentColor"
      />
    </svg>
    <span className="sr-only">{translateInterfaceText("More")}</span>
  </span>
)); }
BreadcrumbEllipsis.displayName = "BreadcrumbEllipsis"

interface BreadcrumbPageProps extends React.HTMLAttributes<HTMLSpanElement> {}
const BreadcrumbPage = ({ className, ...props }: BreadcrumbPageProps) => { useLanguage(); return ((
  <span
    aria-current="page"
    className={cn(
      "text-sm font-medium text-[#64748B] truncate",
      className
    )}
    {...props}
  />
)); }
BreadcrumbPage.displayName = "BreadcrumbPage"

export {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
}
